/**
 * Theme Selector Component
 * Version: 6.2.0
 * Allows users to switch between different themes
 */

import React, { useState } from 'react';
import { useTheme, ThemeName } from '../contexts/ThemeContext';
import './ThemeSelector.css';

interface ThemeSelectorProps {
  className?: string;
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ className = '' }) => {
  const { theme, setTheme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const themeList = Object.values(themes);
  
  const filteredThemes = themeList.filter(
    t => t.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
         t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentTheme = themes[theme];

  const handleThemeSelect = (themeName: ThemeName) => {
    setTheme(themeName);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`theme-selector ${className}`}>
      {/* Current Theme Display */}
      <button 
        className="theme-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change theme"
      >
        <span className="theme-icon">{currentTheme.icon}</span>
        <span className="theme-name">{currentTheme.label}</span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {/* Theme Dropdown */}
      {isOpen && (
        <div className="theme-dropdown">
          <div className="theme-dropdown-header">
            <h3>🎨 Choose Theme</h3>
            <button 
              className="close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Search */}
          <div className="theme-search">
            <input
              type="text"
              placeholder="Search themes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Theme Categories */}
          <div className="theme-categories">
            {/* Dark Themes */}
            <div className="theme-category">
              <div className="category-label">🌙 Dark Themes</div>
              <div className="theme-grid">
                {filteredThemes
                  .filter(t => t.category === 'dark')
                  .map(t => (
                    <button
                      key={t.name}
                      className={`theme-option ${theme === t.name ? 'active' : ''}`}
                      onClick={() => handleThemeSelect(t.name)}
                    >
                      <div className="theme-preview" data-theme={t.name}>
                        <div className="preview-header"></div>
                        <div className="preview-body">
                          <div className="preview-sidebar"></div>
                          <div className="preview-content">
                            <div className="preview-card"></div>
                            <div className="preview-card"></div>
                          </div>
                        </div>
                      </div>
                      <div className="theme-info">
                        <span className="theme-option-icon">{t.icon}</span>
                        <span className="theme-option-label">{t.label}</span>
                      </div>
                      {theme === t.name && <span className="active-badge">✓</span>}
                    </button>
                  ))}
              </div>
            </div>

            {/* Light Theme */}
            <div className="theme-category">
              <div className="category-label">☀️ Light Theme</div>
              <div className="theme-grid">
                {filteredThemes
                  .filter(t => t.category === 'light')
                  .map(t => (
                    <button
                      key={t.name}
                      className={`theme-option ${theme === t.name ? 'active' : ''}`}
                      onClick={() => handleThemeSelect(t.name)}
                    >
                      <div className="theme-preview" data-theme={t.name}>
                        <div className="preview-header"></div>
                        <div className="preview-body">
                          <div className="preview-sidebar"></div>
                          <div className="preview-content">
                            <div className="preview-card"></div>
                            <div className="preview-card"></div>
                          </div>
                        </div>
                      </div>
                      <div className="theme-info">
                        <span className="theme-option-icon">{t.icon}</span>
                        <span className="theme-option-label">{t.label}</span>
                      </div>
                      {theme === t.name && <span className="active-badge">✓</span>}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="theme-dropdown-footer">
            <span className="theme-count">{themeList.length} themes available</span>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && <div className="theme-backdrop" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

export default ThemeSelector;